import "./ErrorPage.scss";
import { lazy, Suspense, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import defaultContent from "../../content/error";
import { IconAmway } from "../../common/icons/IconAmway/IconAmway";
import { IconQuestion } from "../../common/icons/IconQuestion/IconQuestion";
import { IconGlobe } from "../../common/icons/IconGlobe/IconGlobe";
import { IconBrowsers } from "../../common/icons/IconBrowsers/IconBrowsers";
import { IconErrorClock } from "../../common/icons/IconErrorClock/IconErrorClock";
import { IconErrorGeneral } from "../../common/icons/IconErrorGeneral/IconErrorGeneral";
import { IconErrorLock } from "../../common/icons/IconErrorLock/IconErrorLock";
import { IconErrorUnavailable } from "../../common/icons/IconErrorUnavailable/IconErrorUnavailable";
import { IconSearchCriteria } from "../../common/icons/IconSearchCriteria/IconSearchCriteria";
import { browserConfig } from "../../common/constants/browser-constants";
import { store } from "../../store";
import { ROUTE_PATHS } from "../../common/enums/routing-enums";
import LeafButton, { Variant } from "../../common/leaf/LeafButton/LeafButton";
import { IconArrowLink } from "../../common/icons/IconArrowLink/IconArrowLink";
import { track } from "../../common/util/tealium";
import { NewPeriodSelector } from "../Navigation/NewPeriodSelector/NewPeriodSelector";
import {
  setShowErrorPage,
  setAvailableRouteCheckComplete,
  setCurrentPageAvailable,
  setRedirectTo,
} from "../../reducers/componentLoader";
import { ERROR_TYPE, IconType } from "../../common/enums/error-enums";

// Lazy load PageTitleSection to avoid circular dependency
const PageTitleSection = lazy(() =>
  import("../../common/components/PageTitleSection/PageTitleSection").then((module) => ({
    default: module.PageTitleSection,
  })),
);

// Re-export for backwards compatibility
export { ERROR_TYPE, IconType } from "../../common/enums/error-enums";

type Props = {
  errorType?: ERROR_TYPE;
  showPeriodSelector?: boolean;
  iconType?: IconType;
  showBackToAmwayButton?: boolean;
  isLoading?: boolean;
  tealiumTrackFunction?: () => void;
  pageTitle?: string;
  learnMoreLink?: string;
  learnMoreTealiumFunction?: () => void;
  containerClassName?: string;
  asMainLandmark?: boolean;
};

export const ErrorPage = ({
  errorType = ERROR_TYPE.GENERIC_ERROR,
  showPeriodSelector = true,
  iconType = IconType.Frown,
  showBackToAmwayButton = false,
  isLoading = false,
  tealiumTrackFunction,
  pageTitle,
  learnMoreLink,
  learnMoreTealiumFunction,
  containerClassName,
  asMainLandmark = true,
}: Props) => {
  const { t } = useTranslation(["error"]);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorButtonTitle, setErrorButtonTitle] = useState("");
  const [noHeader, setNoHeader] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState(iconType);
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();

  const BrowserIcons = () => {
    const deviceType = store.getState().loginLocalStorage.deviceType || "desktop";
    return (
      <div className="error__browserIcons">
        {browserConfig.map(({ browser, downloadUrl, name }) => {
          const linkText = `${t("downloadBrowser", defaultContent["downloadBrowser"], { browserName: name })}`;
          return (
            <a href={downloadUrl[deviceType]} key={browser} aria-label={linkText}>
              <IconBrowsers browser={browser} />
            </a>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    const page = `${pathname.replace("/", "")}${hash.length > 1 ? hash : ""}`;
    switch (errorType) {
      case ERROR_TYPE.AUTHENTICATION_FAILED:
        setErrorTitle(`${t("authenticationFailedTitle", defaultContent["authenticationFailedTitle"])}`);
        setErrorMessage(`${t("authenticationFailedText", defaultContent["authenticationFailedText"])}`);
        setSelectedIcon(IconType.Lock);
        setNoHeader(true);
        track("view", "", "auth_fail_error_page", "auth_fail_error_page", `errorPage_${page}`);
        break;
      case ERROR_TYPE.CML_LOAD_FAILED:
        setErrorTitle(`${t("cmlLoadFailedTitle", defaultContent["cmlLoadFailedTitle"])}`);
        setErrorMessage(`${t("cmlLoadFailedText", defaultContent["cmlLoadFailedText"])}`);
        break;
      case ERROR_TYPE.COMPONENT_ERROR:
        setErrorTitle(`${t("componentErrorTitle", defaultContent["componentErrorTitle"])}`);
        setErrorMessage(`${t("componentErrorText", defaultContent["componentErrorText"])}`);
        setSelectedIcon(IconType.Clock);
        track("view", "", "component_error_page", "component_error_page", `errorPage_${page}`);
        break;
      case ERROR_TYPE.EDLOS_LOAD_FAILED:
        setErrorTitle(`${t("edLosLoadFailTitle", defaultContent["edLosLoadFailedTitle"])}`);
        setErrorMessage(`${t("edLosLoadFailText", defaultContent["edLosLoadFailedText"])}`);
        break;
      case ERROR_TYPE.INCENTIVE_UNAVAILABLE_FOR_PERIOD:
        setErrorTitle(
          `${t("incentiveUnavailableForPeriodTitle", defaultContent["incentiveUnavailableForPeriodTitle"])}`,
        );
        setErrorMessage(
          `${t("incentiveUnavailableForPeriodText", defaultContent["incentiveUnavailableForPeriodText"])}`,
        );
        setErrorButtonTitle(`${t("dashboard", defaultContent["dashboard"])}`);
        setSelectedIcon(IconType.Clock);
        track("view", "", "invalid_period_error_page", "invalid_period_error_page", `errorPage_${page}`);
        break;
      case ERROR_TYPE.INCORRECT_ACCOUNT:
        setErrorTitle(`${t("incorrectAccountTitle", defaultContent["incorrectAccountTitle"])}`);
        setErrorMessage(`${t("incorrectAccountText", defaultContent["incorrectAccountText"])}`);
        setSelectedIcon(IconType.General);
        setNoHeader(true);
        track("view", "", "incorrect_account_error_page", "incorrect_account_error_page", `errorPage_${page}`);
        break;
      case ERROR_TYPE.NOT_AUTHORIZED:
        setErrorTitle(`${t("notAuthorizedTitle", defaultContent["notAuthorizedTitle"])}`);
        setErrorMessage(`${t("notAuthorizedText", defaultContent["notAuthorizedText"])}`);
        setSelectedIcon(IconType.Lock);
        setNoHeader(true);
        track("view", "", "not_authorized_error_page", "not_authorized_error_page", `errorPage_${page}`);
        break;
      case ERROR_TYPE.PAGE_UNAVAILABLE:
        setErrorTitle(`${t("pageUnavailableTitle", defaultContent["pageUnavailableTitle"])}`);
        setErrorMessage(`${t("pageUnavailableText", defaultContent["pageUnavailableText"])}`);
        setErrorButtonTitle(`${t("btnBackToHome", defaultContent["btnBackToHome"])}`);
        setSelectedIcon(IconType.Unavailable);
        track("view", "", "page_unavailable_error_page", "page_unavailable_error_page", `errorPage_${page}`);
        break;
      case ERROR_TYPE.SYSTEM_MAINTENANCE:
        setErrorTitle(`${t("systemMaintenanceTitle", defaultContent["systemMaintenanceTitle"])}`);
        setErrorMessage(`${t("systemMaintenanceText", defaultContent["systemMaintenanceText"])}`);
        setNoHeader(true);
        break;
      case ERROR_TYPE.UNSUPPORTED_BROWSER:
        setErrorTitle(`${t("unsupportedBrowserTitle", defaultContent["unsupportedBrowserTitle"])}`);
        setErrorMessage(`${t("unsupportedBrowserText", defaultContent["unsupportedBrowserText"])}`);
        track("view", "", "browser_unavailable_error_page", "browser_unavailable_error_page", `errorPage_${page}`);
        setNoHeader(true);
        break;
      case ERROR_TYPE.USER_SESSION_EXPIRED:
        setErrorTitle(`${t("userSessionExpiredTitle", defaultContent["userSessionExpiredTitle"])}`);
        setErrorMessage(`${t("userSessionExpiredText", defaultContent["userSessionExpiredText"])}`);
        setSelectedIcon(IconType.Clock);
        setNoHeader(true);
        track("view", "", "session_timeout_error_page", "session_timeout_error_page", `errorPage_${page}`);
        break;
      case ERROR_TYPE.NO_DOWNLINES:
        setErrorMessage(`${t("noDownlines", defaultContent["noDownlines"])}`);
        setSelectedIcon(IconType.SearchCriteria);
        if (tealiumTrackFunction) {
          tealiumTrackFunction();
        } else {
          track("view", "", "no_downlines_match_criteria", "no_downlines_match_criteria", `errorPage_${page}`);
        }

        break;
      case ERROR_TYPE.GENERIC_ERROR:
      default:
        setErrorTitle(`${t("genericErrorTitle", defaultContent["genericErrorTitle"])}`);
        setErrorMessage(`${t("genericErrorText", defaultContent["genericErrorText"])}`);
        setSelectedIcon(IconType.General);
        track("view", "", "generic_error_page", "generic_error_page", `errorPage_${page}`);
    }
  }, [t, errorType, pathname, hash]);

  const getMainClassName = () => {
    if (isLoading) return "loading error";
    if (noHeader) return "error error__no-header";
    return "error";
  };

  const renderButton = () => {
    if (showBackToAmwayButton || errorButtonTitle) {
      if (!errorButtonTitle) {
        setErrorButtonTitle(`${t("btnBackToAmwayCom", defaultContent["btnBackToAmwayCom"])} `);
      }

      let route = "/";

      switch (errorType) {
        case ERROR_TYPE.INCENTIVE_UNAVAILABLE_FOR_PERIOD:
          route = ROUTE_PATHS.PERFORMANCE_DASHBOARD;
          break;
        case ERROR_TYPE.PAGE_UNAVAILABLE:
          route = ROUTE_PATHS.PERFORMANCE_DASHBOARD;
          break;
        default:
          route = "/";
      }
      return (
        <div id="error__buttons" className="error__buttons">
          <LeafButton
            variant={Variant.SOLID}
            clickAction={() => {
              // Reset flags before navigating
              store.dispatch(setShowErrorPage(false));
              store.dispatch(setAvailableRouteCheckComplete(false));
              store.dispatch(setCurrentPageAvailable(true));
              store.dispatch(setRedirectTo(""));
              navigate(route);
            }}
            id="error-cta-button"
            Icon={<IconArrowLink iconColor="white" />}
          >
            {errorButtonTitle}
          </LeafButton>
        </div>
      );
    }
    return "";
  };

  const renderErrorIcon = () => {
    let icon;
    switch (selectedIcon) {
      case IconType.AmwayLogo:
        icon = <IconAmway useBlackIcon />;
        break;
      case IconType.Question:
        icon = <IconQuestion />;
        break;
      case IconType.Globe:
        icon = <IconGlobe />;
        break;
      case IconType.Clock:
        icon = <IconErrorClock />;
        break;
      case IconType.General:
        icon = <IconErrorGeneral />;
        break;
      case IconType.Lock:
        icon = <IconErrorLock />;
        break;
      case IconType.Unavailable:
        icon = <IconErrorUnavailable />;
        break;
      case IconType.SearchCriteria:
        icon = <IconSearchCriteria />;
        break;
      case IconType.Frown:
      default:
        icon = <IconErrorGeneral />;
    }

    return (
      <div id="error__icon" className="error__icon">
        {icon}
      </div>
    );
  };

  const ErrorContainerTag = asMainLandmark ? "main" : "div";

  const errorContent = (
    <>
      {errorType === ERROR_TYPE.INCENTIVE_UNAVAILABLE_FOR_PERIOD && showPeriodSelector && !pageTitle && (
        <div className="error__mobile-period-selector">
          <NewPeriodSelector />
        </div>
      )}
      <ErrorContainerTag id="error" className={getMainClassName()}>
        {renderErrorIcon()}
        <div id="error__title" className="error__title">
          {errorTitle}
        </div>
        <div id="error__message" className="error__message">
          <Trans>{errorMessage}</Trans>
          {errorType === ERROR_TYPE.UNSUPPORTED_BROWSER && <BrowserIcons />}
        </div>

        {renderButton()}
      </ErrorContainerTag>
    </>
  );

  if (pageTitle) {
    return (
      <div className={containerClassName}>
        <Suspense fallback={null}>
          <PageTitleSection
            title={pageTitle}
            learnMoreLink={learnMoreLink}
            learnMoreTealiumFunction={learnMoreTealiumFunction}
          />
        </Suspense>
        <div className="container">{errorContent}</div>
      </div>
    );
  }

  return errorContent;
};
